import React, { useMemo, useState } from "react";
import type {
  BatchApprovalRequest,
  OperatorPipelineCard,
  OperatorPipelineColumn,
  OperatorPipelineV1,
  PipelineStage,
  SiteId,
} from "@siteagain/shared-types";

import type { Locale } from "../../i18n";
import type { OperatorCopy } from "../copy";
import { relativeTime } from "../format";
import { SiteMark } from "../SiteMark";
import { CHIP } from "./data";
import type { MC2Copy } from "./copy";

export type RecommendationAction = "preview" | "save" | "skip";
export type DecisionAction = "approve" | "reject" | "rollback";

type BoardStage = Exclude<PipelineStage, "saved">;
interface BoardColumn { stage: BoardStage; total: number; cards: OperatorPipelineCard[]; saved: number }

function toColumns(columns: OperatorPipelineColumn[] | undefined): BoardColumn[] {
  const byStage = new Map((columns ?? []).map((column) => [column.stage, column]));
  const detected = byStage.get("detected");
  const saved = byStage.get("saved");
  const merged: BoardColumn = {
    stage: "detected",
    total: (detected?.total ?? 0) + (saved?.total ?? 0),
    cards: [...(detected?.cards ?? []), ...(saved?.cards ?? [])].sort((left, right) => right.occurredAt.localeCompare(left.occurredAt)),
    saved: saved?.total ?? 0,
  };
  const rest = (["building", "to_approve", "deployed", "measured"] as const).map((stage): BoardColumn => {
    const column = byStage.get(stage);
    return { stage, total: column?.total ?? 0, cards: column?.cards ?? [], saved: 0 };
  });
  return [merged, ...rest];
}

export function PipelinePage({
  pipeline,
  copy,
  ops,
  locale,
  now,
  busyKey,
  onOpenSite,
  onRecommendation,
  onDecide,
  onBatch,
  onAddSite,
  refreshSlot,
}: {
  pipeline: OperatorPipelineV1 | null;
  copy: MC2Copy;
  ops: OperatorCopy;
  locale: Locale;
  now: number;
  busyKey: string;
  onOpenSite(siteId: SiteId): void;
  onRecommendation(card: OperatorPipelineCard, action: RecommendationAction): void;
  onDecide(card: OperatorPipelineCard, action: DecisionAction): void;
  onBatch(request: BatchApprovalRequest): void;
  onAddSite(): void;
  refreshSlot: React.ReactNode;
}) {
  const columns = useMemo(() => toColumns(pipeline?.columns), [pipeline]);
  const total = columns.reduce((sum, column) => sum + column.total, 0);
  const eligible = useMemo(
    () => pipeline?.columns.find((column) => column.stage === "to_approve")?.cards.filter((card) => card.batch?.eligible && card.changeSetId && card.batch.evidenceHash) ?? [],
    [pipeline],
  );
  const [expanded, setExpanded] = useState<BoardStage | null>(null);
  const batchRequest = (cards: OperatorPipelineCard[]): BatchApprovalRequest => ({
    items: cards.flatMap((card) => card.changeSetId && card.batch?.evidenceHash
      ? [{ changeSetId: card.changeSetId, evidenceHash: card.batch.evidenceHash }]
      : []),
  });

  return (
    <>
      <div className="mc2-topbar">
        <div>
          <div className="mc2-eyebrow">{copy.pipeline.eyebrow} · {total} {locale === "fr" ? "en cours" : "in flight"}</div>
          <div className="mc2-muted" style={{ fontSize: 13, marginTop: 4 }}>{locale === "fr" ? "Chaque aperçu garde ses preuves et son retour arrière." : "Every preview keeps its evidence and its rollback."}</div>
        </div>
        <div className="mc2-topactions">
          {eligible.length > 0 && <span className={CHIP.lime}>{copy.pipeline.eligible(eligible.length)}</span>}
          {refreshSlot}
          <button type="button" className="mc2-btn" onClick={onAddSite}>+ {locale === "fr" ? "Ajouter un site" : "Add website"}</button>
        </div>
      </div>

      <div className="mc2-pagehead">
        <div>
          <h1>{copy.pipeline.title}</h1>
          <p>{copy.pipeline.subtitle}</p>
        </div>
        <div className="mc2-actions">
          <span className={CHIP.neutral}>{total} {locale === "fr" ? "en cours" : "in flight"}</span>
        </div>
      </div>

      {pipeline === null ? (
        <div className="mc2-loading">{ops.status.loading}</div>
      ) : total === 0 ? (
        <div className="mc2-card"><p className="mc2-empty">{ops.empty.board}</p></div>
      ) : (
        <div className="mc2-boardwrap">
          <div className="mc2-board">
            {columns.map((column) => {
              const shown = expanded === column.stage ? column.cards : column.cards.slice(0, 6);
              const columnEligible = column.stage === "to_approve" ? column.cards.filter((card) => card.batch?.eligible && card.changeSetId && card.batch.evidenceHash) : [];
              return (
                <section className="mc2-col" key={column.stage} aria-label={ops.pipeline.stage[column.stage]}>
                  <div className="mc2-colhead">
                    <span style={{ fontWeight: 600, fontSize: 14 }}>{ops.pipeline.stage[column.stage]}</span>
                    <span className="count">{column.total}</span>
                  </div>
                  <div className="mc2-colhint">{ops.pipeline.hint[column.stage]}</div>
                  {columnEligible.length > 0 && (
                    <BatchCard cards={columnEligible} copy={copy} ops={ops} busy={busyKey === "batch"} onBatch={() => onBatch(batchRequest(columnEligible))} />
                  )}
                  {column.total === 0 ? (
                    <p className="mc2-colhint" style={{ padding: "8px 4px" }}>{copy.pipeline.noCards}</p>
                  ) : shown.map((card) => (
                    <PipelineCard key={card.key} card={card} copy={copy} ops={ops} locale={locale} now={now} busyKey={busyKey} onOpenSite={onOpenSite} onRecommendation={onRecommendation} onDecide={onDecide} />
                  ))}
                  {column.cards.length > 6 && expanded !== column.stage && (
                    <button type="button" className="mc2-linkbtn" style={{ border: 0, background: "transparent", padding: 0, cursor: "pointer", textAlign: "left", fontSize: 13 }} onClick={() => setExpanded(column.stage)}>
                      {copy.pipeline.more(column.cards.length - 6)}
                    </button>
                  )}
                  {column.total > column.cards.length && <p className="mc2-colhint">{copy.pipeline.more(column.total - column.cards.length)}</p>}
                </section>
              );
            })}
          </div>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: 12, marginTop: 20 }}>
        <div className="mc2-card" style={{ padding: "16px 18px" }}>
          <div className="mc2-eyebrow">{locale === "fr" ? "Règle" : "Rule"}</div>
          <div style={{ marginTop: 6 }}>{locale === "fr" ? "Chaque aperçu conserve ses preuves et son retour arrière. Rien ne publie sans approbation." : "Every preview keeps its evidence and its rollback. Nothing publishes without approval."}</div>
        </div>
        <div className="mc2-card" style={{ padding: "16px 18px" }}>
          <div className="mc2-eyebrow">{locale === "fr" ? "Règle" : "Rule"}</div>
          <div style={{ marginTop: 6 }}>{locale === "fr" ? "Un changement arrêté reste visible et réessayable. Un aperçu refusé est une décision, pas une panne." : "A stopped change stays visible and retryable. A declined preview is a decision, not a failure."}</div>
        </div>
        <div className="mc2-card" style={{ padding: "16px 18px" }}>
          <div className="mc2-eyebrow">{locale === "fr" ? "Règle" : "Rule"}</div>
          <div style={{ marginTop: 6 }}>{locale === "fr" ? "Les résultats sont mesurés à 7, 14 et 28 jours, jamais promis à l'avance." : "Results are measured at 7, 14 and 28 days, never promised in advance."}</div>
        </div>
      </div>
    </>
  );
}

function BatchCard({ cards, copy, ops, busy, onBatch }: { cards: OperatorPipelineCard[]; copy: MC2Copy; ops: OperatorCopy; busy: boolean; onBatch(): void }) {
  const [confirming, setConfirming] = useState(false);
  if (confirming) {
    return (
      <div className="mc2-batch" role="group">
        <strong>{copy.pipeline.batchConfirm(cards.length)}</strong>
        <ul>{cards.map((card) => <li key={card.key}>{card.siteName}: {card.title}</li>)}</ul>
        <div className="mc2-actions">
          <button type="button" className="mc2-btn sm" disabled={busy} onClick={() => { setConfirming(false); onBatch(); }}>{busy ? ops.action.working : ops.action.confirm}</button>
          <button type="button" className="mc2-btn ghost sm" onClick={() => setConfirming(false)}>{ops.action.cancel}</button>
        </div>
      </div>
    );
  }
  return (
    <div className="mc2-batch" role="group">
      <strong>{copy.pipeline.eligible(cards.length)}</strong>
      <span style={{ fontSize: 12 }}>{copy.pipeline.batchExplain}</span>
      <button type="button" className="mc2-btn sm" onClick={() => setConfirming(true)}>{copy.pipeline.approveAll}</button>
    </div>
  );
}

function PipelineCard({
  card,
  copy,
  ops,
  locale,
  now,
  busyKey,
  onOpenSite,
  onRecommendation,
  onDecide,
}: {
  card: OperatorPipelineCard;
  copy: MC2Copy;
  ops: OperatorCopy;
  locale: Locale;
  now: number;
  busyKey: string;
  onOpenSite(siteId: SiteId): void;
  onRecommendation(card: OperatorPipelineCard, action: RecommendationAction): void;
  onDecide(card: OperatorPipelineCard, action: DecisionAction): void;
}) {
  const [confirmingRollback, setConfirmingRollback] = useState(false);
  const busy = busyKey.startsWith(`${card.key}:`);
  const working = (action: string) => busyKey === `${card.key}:${action}`;
  const stopped = card.failed && card.changeSetStatus === "failed";
  const declined = card.failed && card.changeSetStatus === "rejected";
  const reasons = card.batch && !card.batch.eligible
    ? card.batch.reasons.filter((reason) => reason !== "not_awaiting_approval").map((reason) => ops.batch.reason[reason])
    : [];
  const meta = [
    card.recommendationId === null ? ops.pipeline.manualChange : null,
    card.stage === "building" && !card.failed && card.changeSetStatus ? ops.pipeline.changeStatus[card.changeSetStatus] : null,
    card.stage === "deployed" ? ops.pipeline.live(relativeTime(card.occurredAt, locale, now)) : ops.pipeline.updated(relativeTime(card.occurredAt, locale, now)),
  ].filter(Boolean).join(" · ");

  return (
    <article className={`mc2-lead${stopped ? " is-stopped" : ""}${declined ? " is-declined" : ""}`}>
      <div className="top">
        <button type="button" style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0, border: 0, background: "transparent", padding: 0, cursor: "pointer", color: "inherit", textAlign: "left" }} onClick={() => onOpenSite(card.siteId)}>
          <SiteMark siteId={card.siteId} name={card.siteName} />
          <span className="site">{card.siteName}</span>
        </button>
      </div>
      <div className="leadtitle">{card.title}</div>

      {stopped && (
        <div className="mc2-stopped" role="alert">
          <strong>{ops.pipeline.stoppedOnError}</strong>
          <span>{card.errorMessage ?? ops.pipeline.noErrorMessage}</span>
        </div>
      )}
      {declined && <div className="mc2-muted" style={{ fontSize: 12 }}>{ops.pipeline.declined}</div>}

      <div className="cardchips">
        {card.tag && <span className={CHIP.neutral}>{ops.pipeline.tag[card.tag]}</span>}
        {card.safetyClass && <span className={CHIP.neutral}>{ops.pipeline.safety[card.safetyClass]}</span>}
        {card.measurement && <span className={CHIP[card.measurement.verdict === "improved" ? "up" : card.measurement.verdict === "declined" ? "down" : "neutral"]}>{card.measurement.windowDays}d · {ops.verdict[card.measurement.verdict]}</span>}
      </div>
      {card.batch?.eligible && <div className="mc2-muted" style={{ fontSize: 12 }}>✓ {ops.pipeline.allChecksPassed}</div>}
      {reasons.length > 0 && <div className="mc2-muted" style={{ fontSize: 12 }}>{ops.pipeline.blockedBy(reasons.length)}: {reasons.join(" · ")}</div>}
      <div className="mc2-metabar">{meta}</div>

      {confirmingRollback ? (
        <div className="mc2-stopped" role="alert">
          <strong>{ops.confirm.rollback}</strong>
          <div className="mc2-actions" style={{ marginTop: 6 }}>
            <button type="button" className="mc2-btn sm" disabled={busy} onClick={() => { setConfirmingRollback(false); onDecide(card, "rollback"); }}>{ops.action.rollback}</button>
            <button type="button" className="mc2-btn ghost sm" onClick={() => setConfirmingRollback(false)}>{ops.action.cancel}</button>
          </div>
        </div>
      ) : (
        <div className="mc2-actions">
          {(card.stage === "detected" || card.stage === "saved") && card.recommendationId && (
            <>
              <button type="button" className="mc2-btn xs" disabled={busy} onClick={() => onRecommendation(card, "preview")}>{working("preview") ? ops.action.working : ops.action.buildPreview}</button>
              {card.stage === "detected" && <button type="button" className="mc2-btn ghost xs" disabled={busy} onClick={() => onRecommendation(card, "save")}>{ops.action.save}</button>}
              <button type="button" className="mc2-btn ghost xs" disabled={busy} onClick={() => onRecommendation(card, "skip")}>{ops.action.dismiss}</button>
            </>
          )}
          {card.stage === "building" && stopped && (
            <>
              {card.recommendationId && <button type="button" className="mc2-btn xs" disabled={busy} onClick={() => onRecommendation(card, "preview")}>{working("preview") ? ops.action.working : ops.action.retry}</button>}
              <button type="button" className="mc2-btn ghost xs" disabled={busy} onClick={() => onOpenSite(card.siteId)}>{ops.action.details}</button>
            </>
          )}
          {card.stage === "building" && card.failed && card.recommendationId && (
            <button type="button" className="mc2-btn ghost xs" disabled={busy} onClick={() => onRecommendation(card, "skip")}>{ops.action.dismiss}</button>
          )}
          {card.stage === "to_approve" && card.changeSetId && (
            <>
              <button type="button" className="mc2-btn xs" disabled={busy} onClick={() => onDecide(card, "approve")}>{working("approve") ? ops.action.working : ops.action.approve}</button>
              <button type="button" className="mc2-btn ghost xs" disabled={busy} onClick={() => onDecide(card, "reject")}>{ops.action.reject}</button>
            </>
          )}
          {card.previewUrl && (card.stage === "to_approve" || card.stage === "building") && (
            <a className="mc2-linkbtn" style={{ fontSize: 12 }} href={card.previewUrl} target="_blank" rel="noreferrer">{ops.action.openPreview} ↗</a>
          )}
          {card.stage === "deployed" && card.changeSetStatus === "deployed" && card.changeSetId && (
            <button type="button" className="mc2-btn ghost xs" disabled={busy} onClick={() => setConfirmingRollback(true)}>{ops.action.rollbackQuiet}</button>
          )}
        </div>
      )}
    </article>
  );
}
